import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import withTranslate from 'containers/widgets/withTranslate';
import Button from 'components/atoms/Button';
import { SECTIONS } from '../constants';
import { formatRelative } from '../utils/dateFormat';
import ConflictItem, { CHOICE_YOURS } from './ConflictItem';
import css from './ConflictDialog.css';

const CONFLICT_PREFIX = 'STATIC.PAGES.AI_CONTEXT.CONFLICT';

const QUESTION_TITLES = SECTIONS.reduce((acc, section) => {
    section.questions.forEach(q => {
        acc[q.id] = q.title;
    });
    return acc;
}, {});

const ConflictDialog = ({ conflict = null, onCancel, onResolve, isResolving = false, translate }) => {
    const open = Boolean(conflict);
    const [choices, setChoices] = useState({});
    const [showBase, setShowBase] = useState({});

    useEffect(() => {
        setChoices({});
        setShowBase({});
    }, [conflict]);

    const conflictCount = conflict?.conflicts?.length ?? 0;
    const allResolved = conflict ? conflict.conflicts.every(item => choices[item.question_id]) : false;

    const resolvedAnswers = useMemo(() => {
        if (!conflict) return {};
        return conflict.conflicts.reduce((acc, item) => {
            acc[item.question_id] = choices[item.question_id] === CHOICE_YOURS ? item.your_answer : item.latest_answer;
            return acc;
        }, {});
    }, [conflict, choices]);

    const handleChoiceChange = (questionId, value) => setChoices(prev => ({ ...prev, [questionId]: value }));
    const handleToggleBase = questionId => setShowBase(prev => ({ ...prev, [questionId]: !prev[questionId] }));

    if (!conflict) return null;

    const latestCreatorText = conflict.latestCreatorEmail
        ? translate(`${CONFLICT_PREFIX}.LATEST_CREATOR`, {
              email: conflict.latestCreatorEmail,
          })
        : translate(`${CONFLICT_PREFIX}.LATEST_CREATOR_FALLBACK`);
    const latestWhen = conflict.latestCreated
        ? formatRelative(conflict.latestCreated)
        : translate(`${CONFLICT_PREFIX}.LATEST_WHEN_FALLBACK`);

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth classes={{ paper: css.dialog }}>
            <DialogTitle className={css.title}>
                {translate(conflictCount === 1 ? `${CONFLICT_PREFIX}.TITLE_ONE` : `${CONFLICT_PREFIX}.TITLE_OTHER`, {
                    count: conflictCount,
                })}
            </DialogTitle>
            <DialogContent className={css.content}>
                <div className={css.intro}>
                    {translate(`${CONFLICT_PREFIX}.INTRO`, {
                        latestCreatorText,
                        latestWhen,
                    })}
                </div>
                <ul className={css.list}>
                    {conflict.conflicts.map(item => (
                        <ConflictItem
                            key={item.question_id}
                            item={item}
                            questionTitle={translate(QUESTION_TITLES[item.question_id] || item.question_id)}
                            choice={choices[item.question_id] || ''}
                            isBaseShown={Boolean(showBase[item.question_id])}
                            onChoiceChange={handleChoiceChange}
                            onToggleBase={handleToggleBase}
                        />
                    ))}
                </ul>
            </DialogContent>
            <DialogActions sx={{ padding: '12px 24px 16px', gap: '8px' }}>
                <Button type="flat" onClick={onCancel} disabled={isResolving}>
                    {translate(`${CONFLICT_PREFIX}.CANCEL`)}
                </Button>
                <Button
                    type="primary"
                    onClick={() => onResolve(resolvedAnswers, conflict.latestVersionId)}
                    showSpinner={isResolving}
                    disabled={isResolving || !allResolved}
                >
                    {translate(`${CONFLICT_PREFIX}.RESOLVE_AND_SAVE`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ConflictDialog.propTypes = {
    conflict: PropTypes.shape({
        baseVersionId: PropTypes.number,
        latestVersionId: PropTypes.number,
        latestCreatorEmail: PropTypes.string,
        latestCreated: PropTypes.string,
        conflicts: PropTypes.arrayOf(
            PropTypes.shape({
                question_id: PropTypes.string.isRequired,
                base_answer: PropTypes.string,
                latest_answer: PropTypes.string,
                your_answer: PropTypes.string,
            })
        ).isRequired,
    }),
    onCancel: PropTypes.func.isRequired,
    onResolve: PropTypes.func.isRequired,
    isResolving: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(ConflictDialog);
